import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import GavelIcon from '@mui/icons-material/Gavel';
import LoginIcon from '@mui/icons-material/Login';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedIcon from '@mui/icons-material/Verified';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import { Box, Button, Container, Fab, Grid, IconButton, Modal, Paper, Typography } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from "react-router-dom";
import './App.css';
import Footer from './Footer';
import './Home.css';
import LawAppBar from './LawAppBar';

const testimonials = [
    {
        quote: "It's so easy to use -- if you can upload a pdf, you can use AttorneyAide.",
        attribution: "Managing Attorney, 3 lawyer Firm, CA"
    },
    {
        quote: "AttorneyAide generates attorney-ready medical chronology in minutes. What took an entire day now takes under an hour.",
        attribution: "Personal Injury Paralegal with 30 years of experience, TX"
    },
    {
        quote: "The automated integrity checks and one-click verification give us confidence nothing gets missed.",
        attribution: "Personal Injury Paralegal, WA"
    }
];

const sampleOutputs = [
    { title: "Medical chronology", image: "/medical_chronology_screenshot.png" },
    { title: "Medical timeline narration", image: "/medical_timeline_screenshot.png" },
    { title: "Medical expense tabulation", image: "/medical_expense_screenshot.png" },
    { title: "AccuAide™ Integrity Engine", image: "/accuaide_screenshot.png" }
];

const threePillars = [
    {
        title: "Instant Accessibility",
        description: "No setup. No contracts. No marketing fluff. Upload PDFs and start immediately.",
        icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />
    },
    {
        title: "Verifiable Accuracy with AccuAide™",
        description: "Issues flagged proactively. Information linked back to the original page for one-click verification.",
        icon: <VerifiedIcon sx={{ fontSize: 40 }} />
    },
    {
        title: "Secure by Design",
        description: "HIPAA-grade encryption. Zero-training privacy. Business Associate Agreement available.",
        icon: <SecurityIcon sx={{ fontSize: 40 }} />
    }
];

const comparisonOldWay = [
    "Weeks spent waiting for medical summaries",
    "Risk of missing a $10k bill in a 500-page stack",
    "Paralegal burnout from repetitive data entry",
    "Overhead-heavy \"per hour\" tasks"
];

const comparisonNewWay = [
    "First draft ready in minutes",
    "Automatic flagging of issues. One click verification.",
    "Paralegals focused on high-value case strategy",
    "Scalable; cost-effective."
];

export default function Home() {
    const [imageModal, setImageModal] = useState({ open: false, src: null, title: '' });

    const navigate = useNavigate();

    const openImageModal = (src, title) => setImageModal({ open: true, src, title });
    const closeImageModal = () => setImageModal({ open: false, src: null, title: '' });

    const ctaButtonStyles = {
        primary: {
            background: 'var(--button-gradient)',
            boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1.05rem',
            padding: '15px 28px',
            borderRadius: '16px',
            minHeight: '56px',
            height: '56px',
            lineHeight: '1.5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            overflow: 'hidden',
            border: 'none',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: '-100%',
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                transition: 'left 0.6s'
            },
            '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 30px rgba(212, 175, 55, 0.6)',
                background: 'var(--button-gradient-hover)'
            },
            '&:hover::before': { left: '100%' },
            '&:active': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)'
            }
        },
        secondary: {
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1.05rem',
            padding: '15px 28px',
            borderRadius: '16px',
            minHeight: '56px',
            height: '56px',
            lineHeight: '1.5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0,
            borderColor: 'var(--border-color)',
            color: 'var(--text-primary)',
            borderWidth: '2px',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
                transform: 'translateY(-2px)',
                borderColor: 'var(--accent-color)',
                borderWidth: '2px',
                backgroundColor: 'var(--surface-color)',
                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.2)'
            }
        }
    };

    return (
        <div className="App">
            <LawAppBar />
            <Container sx={{
                width: "100%",
                maxWidth: "100%",
                padding: "0 20px"
            }}>
                {/* Hero Section */}
                <Box my={2.5} className="welcome-section">
                    <Typography
                        variant="h4"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: '2.4rem',
                            marginBottom: '0.8rem',
                            letterSpacing: '-0.02em'
                        }}
                    >
                        Cut medical record review time by 90%.
                    </Typography>
                    <Box sx={{ maxWidth: 780, margin: '0 auto 1.5rem auto', textAlign: 'center' }}>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'var(--text-secondary)',
                                fontSize: '1.1rem',
                                fontWeight: 400,
                                lineHeight: 1.65,
                                marginBottom: '0.9rem'
                            }}
                        >
                            AttorneyAide automatically organizes medical records into medical chronologies, expense tables, and narrative summaries. Built-in audits and one-click verification.
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: 'var(--text-secondary)',
                                fontSize: '1.1rem',
                                fontWeight: 400,
                                lineHeight: 1.65,
                                marginBottom: 0
                            }}
                        >
                            Let AttorneyAide organize the records so you can focus on the analysis.
                        </Typography>
                    </Box>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: '1rem',
                            fontWeight: 500,
                            lineHeight: 1.55,
                            maxWidth: 780,
                            margin: '0 auto 1rem auto',
                            letterSpacing: '0.01em'
                        }}
                    >
                        HIPAA-grade security • No contracts • No setup • No training required
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'wrap'
                    }}>
                        <Fab
                            variant="extended"
                            size="large"
                            color="primary"
                            onClick={() => navigate('/register')}
                            sx={ctaButtonStyles.primary}
                        >
                            <GavelIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                            Start your free case
                        </Fab>
                        <Button
                            variant="outlined"
                            size="large"
                            onClick={() => navigate('/mycases')}
                            startIcon={<LoginIcon sx={{ fontSize: '1.25rem' }} />}
                            sx={ctaButtonStyles.secondary}
                        >
                            Sign in
                        </Button>
                    </Box>
                    <Paper
                        elevation={0}
                        sx={{
                            display: 'inline-block',
                            borderRadius: "8px",
                            overflow: 'hidden',
                            boxShadow: 'var(--shadow-light)',
                            border: '1px solid var(--border-color)',
                            height: "480px",
                            marginTop: 5,
                            verticalAlign: 'top'
                        }}
                    >
                        <video
                            width="640"
                            height="480"
                            className="demo-video"
                            src="/demo.webm"
                            preload="auto"
                            controls
                            playsInline
                            autoPlay
                            muted
                            loop
                        />
                    </Paper>
                    <Typography
                        sx={{
                            color: "var(--text-secondary)",
                            fontSize: "0.9rem",
                            fontWeight: 300,
                            lineHeight: 1.5,
                            marginTop: 2,
                            marginBottom: 0,
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            maxWidth: 640,
                            textAlign: "center"
                        }}
                    >
                        Demo uses sample documents created for demonstration purposes.
                    </Typography>
                </Box>

                {/* Why Attorneys Love AttorneyAide */}
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 600,
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Why Attorneys Love AttorneyAide
                    </Typography>
                    <Grid container spacing={4} justifyContent="center" sx={{ maxWidth: 1000, margin: '0 auto' }}>
                        {testimonials.map((t, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        height: '100%',
                                        textAlign: 'left',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        flexDirection: 'column'
                                    }}
                                >
                                    <FormatQuoteIcon sx={{ color: 'var(--accent-color)', fontSize: 36, opacity: 0.8, mb: 1 }} />
                                    <Typography sx={{ color: 'var(--text-primary)', fontSize: '1rem', lineHeight: 1.6, flex: 1, mb: 2 }}>
                                        "{t.quote}"
                                    </Typography>
                                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                        — {t.attribution}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Sample Outputs */}
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 600,
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Sample outputs
                    </Typography>
                    <Grid container spacing={3} justifyContent="center" sx={{ maxWidth: 1100, margin: '0 auto' }}>
                        {sampleOutputs.map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        overflow: 'hidden',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '12px',
                                        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 'var(--shadow-medium)'
                                        }
                                    }}
                                >
                                    <Box
                                        className="sample-output-img-wrapper"
                                        component="button"
                                        type="button"
                                        aria-label={`View full size: ${item.title}`}
                                        onClick={() => openImageModal(item.image, item.title)}
                                        sx={{
                                            display: 'block',
                                            width: '100%',
                                            position: 'relative',
                                            height: 160,
                                            backgroundColor: 'var(--surface-color)',
                                            border: 'none',
                                            padding: 0,
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            overflow: 'hidden',
                                            transition: 'opacity 0.2s ease',
                                            '&:hover': { opacity: 0.9 },
                                            '& .sample-img': { width: '100%', height: 160, objectFit: 'cover' },
                                            '& .sample-overlay': {
                                                position: 'absolute',
                                                top: 0, left: 0, right: 0, bottom: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                opacity: 0,
                                                backgroundColor: 'rgba(0,0,0,0.35)',
                                                transition: 'opacity 0.25s ease'
                                            },
                                            '&:hover .sample-overlay': { opacity: 1 },
                                            '& .sample-fallback': {
                                                display: 'none',
                                                position: 'absolute',
                                                top: 0, left: 0, right: 0, bottom: 0,
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            },
                                            '&.img-failed .sample-img': { display: 'none' },
                                            '&.img-failed .sample-fallback': { display: 'flex' }
                                        }}
                                    >
                                        <img
                                            className="sample-img"
                                            src={item.image}
                                            alt={item.title}
                                            onError={(e) => e.target.closest('.sample-output-img-wrapper')?.classList.add('img-failed')}
                                        />
                                        <Box className="sample-overlay">
                                            <ZoomInIcon sx={{ color: 'white', fontSize: 48 }} />
                                        </Box>
                                        <Box className="sample-fallback">
                                            <Typography sx={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{item.title}</Typography>
                                        </Box>
                                    </Box>
                                    <Typography sx={{ p: 2, color: 'var(--text-primary)', fontWeight: 500, fontSize: '0.95rem' }}>
                                        {item.title}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                    <Typography
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: '0.9rem',
                            fontWeight: 400,
                            lineHeight: 1.5,
                            margin: '24px auto 0',
                            textAlign: 'center'
                        }}
                    >
                        All samples shown are generated from fictional case materials for demonstration purposes only.
                    </Typography>
                </Box>

                {/* Our Three Pillars */}
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 600,
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Our Three Pillars
                    </Typography>
                    <Grid container spacing={4} justifyContent="center" sx={{ maxWidth: 1000, margin: '0 auto' }}>
                        {threePillars.map((pillar, i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        height: '100%',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '12px',
                                        textAlign: 'center'
                                    }}
                                >
                                    <Box sx={{ color: 'var(--accent-color)', mb: 2 }}>{pillar.icon}</Box>
                                    <Typography variant="h6" sx={{ color: 'var(--text-primary)', fontWeight: 600, mb: 1.5 }}>
                                        {pillar.title}
                                    </Typography>
                                    <Typography sx={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: 1.6 }}>
                                        {pillar.description}
                                    </Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Why PI Firms Choose Us */}
                <Box sx={{ py: 6, textAlign: 'center' }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 600,
                            marginBottom: 4,
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Why PI Firms Choose Us
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            maxWidth: 700,
                            margin: '0 auto',
                            overflow: 'hidden',
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px'
                        }}
                    >
                        <Grid container>
                            <Grid item xs={12} sm={6} sx={{ borderRight: { sm: '1px solid var(--border-color)' } }}>
                                <Box sx={{ p: 3, backgroundColor: 'var(--surface-color)', minHeight: 200 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--text-secondary)', mb: 2 }}>
                                        The Old Way
                                    </Typography>
                                    {comparisonOldWay.map((item, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5, textAlign: 'left' }}>
                                            <Typography sx={{ color: 'var(--error-color)', fontSize: '1.25rem', lineHeight: 1 }}>×</Typography>
                                            <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>{item}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ p: 3, minHeight: 200 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'var(--text-primary)', mb: 2 }}>
                                        The AttorneyAide Way
                                    </Typography>
                                    {comparisonNewWay.map((item, i) => (
                                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5, textAlign: 'left' }}>
                                            <CheckCircleIcon sx={{ color: 'var(--success-color)', fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                                            <Typography sx={{ color: 'var(--text-primary)', fontSize: '0.95rem' }}>{item}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Box>

                {/* Final CTA */}
                <Box sx={{ py: 6, mb: 8, textAlign: 'center' }}>
                    <Typography
                        variant="h5"
                        component="h2"
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 600,
                            marginBottom: 2,
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Ready to reclaim your billable hours?
                    </Typography>
                    <Fab
                        variant="extended"
                        size="large"
                        color="primary"
                        onClick={() => navigate('/mycases')}
                        sx={{
                            background: 'var(--button-gradient)',
                            boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1.05rem',
                            padding: '15px 28px',
                            borderRadius: '16px',
                            minHeight: '56px',
                            height: '56px',
                            lineHeight: '1.5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 'auto',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            border: 'none',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 30px rgba(212, 175, 55, 0.6)',
                                background: 'var(--button-gradient-hover)'
                            },
                            '&:active': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)'
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                transition: 'left 0.6s'
                            },
                            '&:hover::before': {
                                left: '100%'
                            }
                        }}
                    >
                        <GavelIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                        Try It Now
                    </Fab>
                </Box>
            </Container>

            {/* Image lightbox modal */}
            <Modal
                open={imageModal.open}
                onClose={closeImageModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: 'rgba(0,0,0,0.85)',
                            backdropFilter: 'blur(4px)'
                        }
                    }
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        outline: 'none'
                    }}
                >
                    <Box
                        sx={{
                            position: 'relative',
                            backgroundColor: 'white',
                            borderRadius: 1,
                            overflow: 'visible',
                            boxShadow: '0 24px 80px rgba(0,0,0,0.5)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <IconButton
                            onClick={closeImageModal}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 1,
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.6)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                            }}
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </IconButton>
                        <img
                            src={imageModal.src || ''}
                            alt={imageModal.title}
                            style={{
                                display: 'block',
                                maxWidth: '90vw',
                                maxHeight: '85vh',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain'
                            }}
                        />
                        {imageModal.title && (
                            <Typography
                                sx={{
                                    p: 1.5,
                                    color: 'var(--text-primary, #1a1a1a)',
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    textAlign: 'center',
                                    borderTop: '1px solid var(--border-color, rgba(0,0,0,0.12))'
                                }}
                            >
                                {imageModal.title}
                            </Typography>
                        )}
                    </Box>
                    <Typography
                        sx={{
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '0.85rem',
                            textAlign: 'center',
                            mt: 1.5
                        }}
                    >
                        Click outside or press Esc to close
                    </Typography>
                </Box>
            </Modal>

            <Footer />
        </div>
    );
}
